import { type AtLeast, type Dayjs, RequestError } from '@vartion/ui'
import { acceptHMRUpdate, defineStore } from 'pinia'

import { bus } from '~/services/bus.ts'
import { IndexedDBCache } from '~/services/cache/IndexedDBCache.ts'
import {
  CASE_SOURCE_NAMES,
  type CaseHit,
  type CaseModelDetailed,
  type CaseModelSearchMode,
  type CaseSource,
  type CaseSourceName,
  type CaseType,
  type CreateCaseForm,
  type HitData,
  type HitResolution,
  type SearchResponse,
} from '~/types.ts'
import { type UpdateCaseRequest, useCases } from './cases.ts'
import { createHitsStore } from './hits.ts'
import { pinia, withReset } from './index.ts'
import { canReadCase } from './user.ts'

interface User {
  id: number
  name: string
}

export type CaseOrigin = 'person-check' | 'business-check' | 'asset-check' | 'hyperlink'

export const FORM_KEYS = [
  'name',
  'aliases',
  'additional_terms',
  'excluded_terms',
  'gender',
  'date_of_birth',
  'nationalities',
  'country_of_birth',
  'country_of_residence',
  'address',
  'country',
  'company_number',
  'asset_type',
  'identifier',
] as const

export interface UpdateCaseParams {
  form?: Record<string, any>
  passportVerificationForm?: Record<string, any>
  searchMode?: CaseModelSearchMode
}

export interface CreateCaseParams {
  form: AtLeast<CreateCaseForm, 'name' | 'type'>
  passportVerificationForm?: Record<string, any>
  origin?: CaseOrigin
  originCaseId?: null | number
  duplicateCheck?: boolean
}

export interface SearchRequest {
  type: CaseType
  origin?: CaseOrigin
  origin_case_id?: null | number
  duplicateCheck?: boolean
  passportVerificationForm?: Record<string, any>
  case_id?: number
  [key: string]: any
}

export function createCaseStore(storeName: string) {
  return defineStore(storeName, () => {
    const caseModel = ref<CaseModelDetailed>()
    const originCaseId = ref<null | number>(null)
    const activeResolution = ref<HitResolution>('unresolved')
    const searchedName = ref('')
    const users = ref<User[]>([])
    const { filters, filtersActive } = useFilters({
      query: '',
      names: [] as string[],
      created_at: [] as string[],
      adverse: null as null | string,
      publish_date: null as null | string,
      industries: null as null | string,
      has_update: false,
      negative_events: null as null | string,
      country: [] as (string | null)[],
    })
    const filtersVisible = ref(false)
    const interval = ref<undefined | number>(undefined)
    const lastSearchedAtStarted = ref<Dayjs>()

    const cases = useCases()

    const enforcements = createHitsStore('enforcements', storeName)()
    const news = createHitsStore('news', storeName)()
    const other = createHitsStore('other', storeName)()
    const peps = createHitsStore('peps', storeName)()
    const sanctions = createHitsStore('sanctions', storeName)()

    const sourceMap = {
      enforcements,
      news,
      other,
      peps,
      sanctions,
    }

    const $reset = withReset({
      caseModel,
      originCaseId,
      activeResolution,
      searchedName,
      users,
      filtersVisible,
      interval,
    })

    IndexedDBCache.tags('UIState')
      .get('resolveFiltersVisible')
      .then((value) => (filtersVisible.value = value ?? false))

    watch(
      () => filtersVisible.value,
      (newValue) => IndexedDBCache.tags('UIState').put('resolveFiltersVisible', newValue),
    )

    const name = computed((): string => (caseModel.value ? `${caseModel.value.type} Check` : ''))

    const sources = computed((): CaseSourceName[] => {
      return caseModel.value ? (stores.source.availablePerType[caseModel.value.type] ?? []) : []
    })

    const isSearching = computed((): boolean => sources.value.some((source) => sourceMap[source].loading.length > 0))

    watch(isSearching, async (searching) => {
      if (searching) {
        pollForUpdates()
      } else if (caseModel.value) {
        clearInterval(interval.value)
        // When searching is finished, get the latest audits and unresolved risk of the case.
        const { data } = await api.get(`cases/${caseModel.value.id}`)
        Object.assign(caseModel.value, data)
        bus.emit('caseAudits.fetch')
      }
    })

    /** Create a case. Resets the store if it already contains a case. */
    async function create({ form, passportVerificationForm = {}, origin, originCaseId: _originCaseId = null, duplicateCheck = true }: CreateCaseParams) {
      await stores.loading.update({ case: true })

      if (!origin) {
        origin = form.type === 'Person' ? 'person-check' : form.type === 'Business' ? 'business-check' : 'asset-check'
      }

      if (!_originCaseId) {
        _originCaseId = originCaseId.value
      } else {
        originCaseId.value = _originCaseId
      }

      reset()

      searchedName.value = form.name

      const postData: SearchRequest = {
        ...form,
        origin,
        origin_case_id: _originCaseId,
        duplicate_check: duplicateCheck,
      }

      if (form.type === 'Person') {
        postData.passportVerificationForm = passportVerificationForm
      }

      try {
        const { data } = await api.post<CaseModelDetailed>('cases', postData)
        if (!canReadCase(data)) {
          $message.success($t('the-case-was-created-but-you-do-not-have-access-to-view-it'))

          return
        }

        updateCase(data)
      } catch (error) {
        if (error instanceof RequestError) {
          const status = error.response?.status
          const data = error.response?.data as { uuids: string[]; error: string; message: string }
          if (status === 403) {
            await duplicateCasesFound({ ...data, searchData: { form, origin, originCaseId: originCaseId.value, passportVerificationForm } })
          } else if (status === 422) {
            $message.error(data.message)
          } else {
            $message.error($t('api-error'))
            console.error(error)
          }
        } else {
          $message($t('api-error'))
          console.error(error)
        }
      } finally {
        await stores.loading.update({ case: false })
      }
    }

    async function update({ form = {}, passportVerificationForm = {}, searchMode = 'update' }: UpdateCaseParams) {
      if (!caseModel.value) {
        throw new Error('Missing case to update.')
      }

      await stores.loading.update({ case: true })

      const type = caseModel.value.type
      searchedName.value = form.name

      const payload: UpdateCaseRequest = {
        ...form,
        reset_searched_at: searchMode === 'full',
      }

      if (type === 'Person') {
        payload.passportVerificationForm = passportVerificationForm
      }

      try {
        const data = await cases.update(caseModel.value.id, payload)
        if (!data) throw new Error('Update failed.')
        Object.assign(caseModel.value, data)

        // Unsearched sources should always search.
        let activeSources = data.sources.filter((source) => source.active && !source.searched_at)

        if (searchMode === 'full' || searchMode === 'update') {
          activeSources = data.sources.filter((source) => source.active)
        }

        if (activeSources.length > 0) {
          searchSources({ sources: activeSources })
        }

        const deactivatedSources = data.sources.filter((source) => !source.active)
        for (const source of deactivatedSources) {
          sourceMap[source.data_source.source].deactivateSource(source.id)
        }
      } catch {
        $message($t('api-error'))
      } finally {
        await stores.loading.update({ case: false })
      }
    }

    async function loadCase(data: CaseModelDetailed) {
      // Reset the state to a clean one before loading in a new state.
      if (caseModel.value) {
        reset()
      }

      searchedName.value = data.name

      updateCase(data)
      await delay(300) // Wait a bit to make sure the page has been created, any awaits on this action can then send events via the bus.

      for (const source of sources.value) {
        sourceMap[source].loadHitsFromCase(data)
      }

      if (data.status !== 'Archived') {
        const unsearched = data.sources.filter((source) => source.active && (!source.searched_at || !source.succeeded))
        if (unsearched.length > 0) {
          try {
            searchSources({ sources: unsearched })
          } catch {
            // Most likely means there are no searches remaining.
          }
        }
      }
    }

    async function searchSources({ sources, resetSearchedAt = false }: { sources?: CaseSource[]; resetSearchedAt?: boolean } = {}) {
      if (!sources) {
        sources = caseModel.value?.sources.filter((source) => source.active && source.data_source.active) ?? []
        if (!stores.organization.has_data_directory) {
          sources = sources.filter((source) => source.data_source.vendor !== 'custom')
        }
      }

      lastSearchedAtStarted.value = dayjs().utc().startOf('second')
      sources.forEach((source) => sourceMap[source.data_source.source].searching(source))
      try {
        const { data } = await api.post(
          'case-sources/search',
          {
            ids: sources.map((source) => source.id),
            case_id: caseModel.value?.id,
            reset_searched_at: resetSearchedAt,
          },
          { key: null },
        )
        lastSearchedAtStarted.value = dayjs(data.searched_at).utc().startOf('second').subtract(1, 'second')
      } catch (error) {
        sources.forEach((source) => sourceMap[source.data_source.source].setError(source.id))
        throw error
      }
    }

    // Reset the state and the state of all source modules.
    function reset() {
      resetState()

      for (const source of CASE_SOURCE_NAMES) {
        sourceMap[source].$reset()
      }
    }

    function resetState() {
      const oldFiltersVisible = filtersVisible.value
      echo.leave(`App.Models.CaseModel.${caseModel.value?.id}`)
      clearInterval(interval.value)
      $reset()
      filters.reset()
      filtersVisible.value = oldFiltersVisible
    }

    function updateCase(newCase: CaseModelDetailed) {
      if (newCase.id === caseModel.value?.id) {
        return
      }

      if (caseModel.value) {
        echo.leaveChannel(`presence-App.Models.CaseModel.${caseModel.value.id}`)
        clearInterval(interval.value)
      }

      caseModel.value = newCase
      listenForEvents()
    }

    async function duplicateCasesFound({ error, uuids, searchData }: { error: string; uuids: string[]; searchData: CreateCaseParams }) {
      blurAll() // Remove focus on input, because else it searches twice.

      try {
        await $confirm({
          message: $t(
            'a-case-with-the-same-information-you-provided-already-exists-are-you-sure-you-want-to-create-the-same-case-again-or-open-the-identical-existing-case-or-count-cases-with-the-same-information-you-provided-already-exist-are-you-sure-you-want-to-create-the-same-case-again-or-view-the-identical-existing-cases',
            uuids.length,
          ),
          title: error,
          confirmButtonLabel: $t('create-new-case'),
          cancelButtonLabel: uuids.length === 1 ? $t('open-existing-case') : $t('view-existing-cases'),
        })
      } catch (error) {
        if (isConfirmError(error) && error === 'Cancel button') {
          if (uuids.length > 1) {
            await $router.push('/cases')
            await delay(300) // Wait for route transition.
            bus.emit('cases.updateFilters', { updates: { uuid: uuids }, reset: true })
          } else {
            const item = await cases.load(uuids[0], [
              'caseUsers',
              'caseGroups',
              'passportVerification',
              'clients',
              'labels',
              'hits.comments',
              'hits.labels',
              'sources.dataSource:id,source,vendor,active,deactivated_at',
              'user',
              'group',
              'organization',
            ])
            if (item) {
              loadCase(item)
            }
          }
        } else {
          searchedName.value = ''
        }

        return
      }

      await create({ ...searchData, duplicateCheck: false })
    }

    function listenForEvents() {
      echo.leaveChannel(`presence-App.Models.CaseModel.${caseModel.value?.id}`)

      echo
        .join(`App.Models.CaseModel.${caseModel.value?.id}`)
        .here((_users: User[]) => (users.value = _users))
        .joining((user: User) => addUser(user))
        .leaving((user: User) => removeUser(user))
        .listen('CaseUpdated', ({ case: data }: { case: CaseModelDetailed }) => {
          if (caseModel.value) Object.assign(caseModel.value, data)
        })
        .listen('CaseAssigned', ({ case: data }: { case: CaseModelDetailed }) => {
          if (caseModel.value) Object.assign(caseModel.value, { user_id: data.user_id, group_id: data.group_id, updated_at: dayjs().toISOString() })
        })
        .listen('HitsUpdated', ({ hits, risk, unresolved_risk: unresolvedRisk }: { hits: CaseHit[]; risk: number; unresolved_risk: number }) => {
          for (const source of CASE_SOURCE_NAMES) {
            const sourceHits = hits.filter((hit) => hit.source === source)
            if (sourceHits) {
              sourceMap[source].updateHits(sourceHits)
            }
          }

          if (caseModel.value && (risk !== caseModel.value.risk || unresolvedRisk !== caseModel.value.unresolved_risk)) {
            Object.assign(caseModel.value, { risk, unresolved_risk: unresolvedRisk, updated_at: dayjs().toISOString() })
          }
        })
        .listen('CaseSourceSearched', ({ hits, source }: { hits: CaseHit[]; source: CaseSource }) => {
          if (caseModel.value) caseModel.value.searched_at = dayjs().toISOString()
          sourceMap[source.data_source.source].loadHitsFromSearch({ source, hits: hits.filter((hit) => hit.data_source_id === source.data_source_id) })
        })
        .listen('CaseSourceSearchFailed', ({ source }: { source: CaseSource }) => {
          const name = source.data_source.source
          $message.error(`Failed to search ${stores.source.labels[name]}`)
          sourceMap[source.data_source.source].setError(source.id)
        })
    }

    function pollForUpdates() {
      clearInterval(interval.value)
      interval.value = setInterval(async () => {
        const id: number[] = []

        for (const source of sources.value) {
          id.push(...sourceMap[source].loading)
        }

        if (id.length === 0 || !caseModel.value || !lastSearchedAtStarted.value) return

        const { data } = await api.post<SearchResponse<CaseSource & { hits: CaseHit[] }>>(
          'case-sources/searches',
          {
            id,
            searched_at: [lastSearchedAtStarted.value.format('YYYY-MM-DD HH:mm:ss'), dayjs().utc().add(1, 'minute').format('YYYY-MM-DD HH:mm:ss')],
            with: ['hits.comments', 'hits.labels', 'dataSource'],
            per_page: id.length,
          },
          { key: `${storeName}:case-sources/searches` },
        )

        for (const source of data.data) {
          sourceMap[source.data_source.source].updateSource(source)
        }
      }, 5000) as unknown as number
    }

    function addUser(user: User) {
      if (users.value.findIndex((item) => item.id === user.id) === -1) {
        users.value.push(user)
      }
    }

    function removeUser(user: User) {
      const index = users.value.findIndex((item) => item.id === user.id)
      if (index >= 0) {
        users.value.splice(index, 1)
      }
    }

    function disposeChildren() {
      echo.leaveChannel(`presence-App.Models.CaseModel.${caseModel.value?.id}`)

      for (const source of CASE_SOURCE_NAMES) {
        sourceMap[source].$dispose()
        delete pinia.state.value[sourceMap[source].$id]
      }
    }

    function hitHasChanges(hit: CaseHit): boolean {
      if (hit.update_changes) {
        return true
      }

      if (hit.similar_data_ids) {
        const hitsByDataId = sourceMap[hit.source].hitsByDataId

        return hit.similar_data_ids?.some((id) => Boolean(hitsByDataId[id]?.update_changes)) || false
      }

      return false
    }

    function isHitFilteredByCreatedAt(hit: HitData): boolean {
      const createdAt = filters.created_at

      if (createdAt[0] && dayjs(hit.case_hit?.created_at).isBefore(dayjs(createdAt[0]), 'day')) return true
      if (createdAt[1] && dayjs(hit.case_hit?.created_at).isAfter(dayjs(createdAt[1]), 'day')) return true

      return false
    }

    function isHitFilteredByNames(hit: HitData): boolean {
      const names = filters.names
      if (names.length === 0 || !hit.highlight) {
        return false
      }

      return !Object.values(hit.highlight).some((highlightTypes) => {
        if (!Array.isArray(highlightTypes)) {
          delete highlightTypes['addresses.text']
          delete highlightTypes['identifiers.value']
        }

        return Object.values(highlightTypes).some((highlights) =>
          ((typeof highlights === 'string' ? [highlights] : highlights) as string[]).some((highlight) =>
            names.includes(highlight.replaceAll('<em>', '').replaceAll('</em>', '').toLowerCase()),
          ),
        )
      })
    }

    function isHitFilteredByQuery(hit: HitData): boolean {
      if (!filters.query) return false

      const array: any[] = []

      for (const key in hit) {
        if (key === 'case_hit') {
          array.push(hit[key]?.comments)
          array.push(hit[key]?.labels.map((label) => label.name))
          for (const updates of Object.values(hit[key]?.update_changes ?? {})) {
            for (const [field, update] of Object.entries(updates)) {
              if (field !== 'case_hit') {
                array.push(update.new)
                array.push(update.old)
              }
            }
          }
        } else {
          array.push(hit[key])
        }
      }

      return !stringInArray({ array, string: filters.query })
    }

    function isHitFilteredByUpdate(hit: HitData): boolean {
      return filters.has_update && activeResolution.value !== 'unresolved' && (!hit.case_hit || !hitHasChanges(hit.case_hit))
    }

    function isHitFilteredByIndustries(hit: HitData): boolean {
      if (!filters.industries || hit.case_hit?.source !== 'news') {
        return false
      }

      return !hit.industries || !hit.industries.includes(filters.industries)
    }

    function isHitFilteredByAdverse(hit: HitData): boolean {
      if (!filters.adverse || hit.case_hit?.source !== 'news') {
        return false
      }

      return !hit.adverse || !(filters.adverse in hit.adverse) || hit.adverse[filters.adverse] < stores.initialState.adverse.minimum_threshold
    }

    function isHitFilteredByNegativeEvents(hit: HitData): boolean {
      if (!filters.negative_events || hit.case_hit?.source !== 'news') {
        return false
      }

      return !hit.negative_events || !hit.negative_events.includes(filters.negative_events)
    }

    function isHitFilteredByPublishDate(hit: HitData): boolean {
      if (!filters.publish_date || hit.case_hit?.source !== 'news') {
        return false
      }

      if (filters.publish_date === $t('missing')) {
        return Boolean(hit.publish_date)
      }

      return !hit.publish_date || filters.publish_date !== hit.publish_date.substring(0, 4)
    }

    function isHitFilteredByCountry(hit: HitData): boolean {
      if (filters.country.length === 0 || hit.case_hit?.source !== 'news') {
        return false
      }

      const hitCountryAlpha3 = hit.country ? (stores.countries.country(hit.country)?.alpha_3 ?? null) : null

      return !filters.country.includes(hitCountryAlpha3)
    }

    function isHitFilteredBySimilarHit(hit: HitData): boolean {
      if (hit.case_hit?.source !== 'news') {
        return false
      }

      return news.similarHitIds.has(hit.id as string)
    }

    function isHitFiltered(hit: HitData): boolean {
      return (
        isHitFilteredByCreatedAt(hit) ||
        isHitFilteredByNames(hit) ||
        isHitFilteredByQuery(hit) ||
        isHitFilteredByUpdate(hit) ||
        isHitFilteredByIndustries(hit) ||
        isHitFilteredByAdverse(hit) ||
        isHitFilteredByNegativeEvents(hit) ||
        isHitFilteredByPublishDate(hit) ||
        isHitFilteredByCountry(hit) ||
        isHitFilteredBySimilarHit(hit)
      )
    }

    return {
      activeResolution,
      case: caseModel,
      create,
      disposeChildren,
      enforcements,
      filters,
      filtersActive,
      hitHasChanges,
      interval,
      isHitFiltered,
      isHitFilteredByAdverse,
      isHitFilteredByIndustries,
      isHitFilteredByNegativeEvents,
      isHitFilteredByPublishDate,
      isSearching,
      lastSearchedAtStarted,
      loadCase,
      name,
      news,
      originCaseId,
      other,
      peps,
      reset,
      sanctions,
      searchedName,
      searchSources,
      filtersVisible,
      sources,
      update,
      users,
    }
  })
}

export const useCase = createCaseStore('case')

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCase, import.meta.hot))
}
